import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StytchProvider } from './providers/StytchProvider';
import { AuthProvider, ProtectedRoute, AdminRoute, PermissionRoute } from './contexts/AuthProvider';
import { RequirePlan } from './shared/components/routing/RequirePlan';
import { RequirePremium } from './shared/components/routing/RequirePremium';
import { Sidebar } from './shared/components/layout/Sidebar';
import { ROUTES } from './shared/constants';
import { Toaster } from 'sonner';
import './styles/toaster.css';
import { DefaultRedirect } from './shared/components/DefaultRedirect';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { Loader2 } from 'lucide-react';

// Lazy load pages for code splitting and faster initial load
const QuestionsManagement = lazy(() => import('./pages/questionsManagement/QuestionsManagement').then(m => ({ default: m.QuestionsManagement })));
const QuestionGenerator = lazy(() => import('./pages/questionGenerator/QuestionGenerator').then(m => ({ default: m.QuestionGenerator })));
const QuestionDemo = lazy(() => import('./pages/questionDemo/QuestionDemo').then(m => ({ default: m.QuestionDemo })));
const ExamDemo = lazy(() => import('./pages/examDemo/ExamDemo').then(m => ({ default: m.ExamDemo })));
const ExamHistory = lazy(() => import('./pages/examHistory/ExamHistory').then(m => ({ default: m.ExamHistory })));
const ExamSession = lazy(() => import('./pages/examSession/ExamSession').then(m => ({ default: m.ExamSession })));
const ExamSessionNBME = lazy(() => import('./pages/examSession/ExamSessionNBME').then(m => ({ default: m.ExamSessionNBME })));
const ExamResults = lazy(() => import('./pages/examResults/ExamResults').then(m => ({ default: m.ExamResults })));
const Review = lazy(() => import('./pages/review/Review').then(m => ({ default: m.Review })));
const AdminManagement = lazy(() => import('./pages/admin/AdminManagement').then(m => ({ default: m.AdminManagement })));
const Login = lazy(() => import('./pages/auth').then(m => ({ default: m.Login })));
const Signup = lazy(() => import('./pages/auth').then(m => ({ default: m.Signup })));
const VerifyAccount = lazy(() => import('./pages/auth').then(m => ({ default: m.VerifyAccount })));
const ResetPassword = lazy(() => import('./pages/auth').then(m => ({ default: m.ResetPassword })));
const MagicLinkCallback = lazy(() => import('./pages/auth/MagicLinkCallback').then(m => ({ default: m.MagicLinkCallback })));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const SSOCallback = lazy(() => import('./pages/auth/SSOCallback').then(m => ({ default: m.SSOCallback })));
const Users = lazy(() => import('./pages/users').then(m => ({ default: m.Users })));
const BillingPage = lazy(() => import('./pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const BillingProcessing = lazy(() => import('./pages/billing/BillingProcessing').then(m => ({ default: m.BillingProcessing })));
const Budget = lazy(() => import('./pages/budget').then(m => ({ default: m.Budget })));
const FinancialDashboard = lazy(() => import('./pages/financial').then(m => ({ default: m.FinancialDashboard })));
const TestExamApi = lazy(() => import('./pages/testExamApi').then(m => ({ default: m.TestExamApi })));
const Onboarding = lazy(() => import('./pages/onboarding/Onboarding').then(m => ({ default: m.Onboarding })));
const Demo = lazy(() => import('./pages/demo/Demo').then(m => ({ default: m.Demo })));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const Settings = lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })));
const Landing = lazy(() => import('./pages/landing').then(m => ({ default: m.Landing })));
const Analytics = lazy(() => import('./pages/analytics/Analytics').then(m => ({ default: m.Analytics })));
// const PublicProfile = lazy(() => import('./pages/publicProfile/PublicProfile').then(m => ({ default: m.PublicProfile }))); // Disabled for now
const SelectPlan = lazy(() => import('./pages/selectPlan/SelectPlan').then(m => ({ default: m.SelectPlan })));
const TermsOfService = lazy(() => import('./pages/legal').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/legal').then(m => ({ default: m.PrivacyPolicy })));

// Loading fallback component
function PageLoader() {
  return (
    <div className="h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
      <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-brand-400" />
    </div>
  );
}

// Create a client with optimized caching strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes (reasonable default for most data)
      gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache longer)
      refetchOnWindowFocus: true, // Refresh when user returns to tab
      refetchOnMount: false, // Don't refetch if data is fresh
      refetchOnReconnect: true, // Refetch when internet reconnects
      retry: 2, // Retry failed requests twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
  },
});

// Layout component for authenticated pages
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  return (
    <div className="h-screen bg-neutral-50 dark:bg-neutral-900 flex">
      {/* Sidebar */}
      <Sidebar currentPath={location.pathname} />
      
      {/* Main Content */}
      <main className="flex-1 h-screen overflow-y-auto relative bg-neutral-50 dark:bg-neutral-900">
        {children}
      </main>
    </div>
  );
}

function App() {
  
  return (
    <QueryClientProvider client={queryClient}>
      <StytchProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <Toaster
              position="bottom-left"
              expand={false}
              richColors={false}
              closeButton={false}
              toastOptions={{
                classNames: {
                  toast: 'toast-custom',
                  title: 'toast-title',
                  description: 'toast-description',
                  actionButton: 'toast-action',
                  cancelButton: 'toast-cancel',
                },
              }}
            />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public Landing Page */}
              <Route path="/landing" element={<Landing />} />

            {/* Public Routes */}
            <Route path="/demo" element={<Demo />} />
            {/* <Route path="/profile/:userUuid" element={<PublicProfile />} /> */} {/* Disabled for now */}

            {/* Public Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
            <Route path="/verify" element={<VerifyAccount />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Auth Callback Routes */}
            <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
            <Route path="/auth/oauth-callback" element={<OAuthCallback />} />
            <Route path="/auth/sso" element={<SSOCallback />} />

            {/* Legal Pages */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Redirect old auth paths */}
            <Route path="/auth/login" element={<Navigate to="/login" replace />} />
            <Route path="/auth/signup" element={<Navigate to="/signup" replace />} />
            <Route path="/auth/forgot-password" element={<Navigate to="/login" replace />} />
            <Route path="/auth/verify" element={<Navigate to="/verify" replace />} />
            <Route path="/auth/reset-password" element={<Navigate to="/reset-password" replace />} />

            {/* Onboarding Route (Protected but no layout) */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireOnboarding={false}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Plan Selection Route (Protected but no layout) */}
            <Route
              path="/select-plan"
              element={
                <ProtectedRoute>
                  <SelectPlan />
                </ProtectedRoute>
              }
            />

            {/* Dashboard Route */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RequirePlan>
                    <AuthenticatedLayout>
                      <Dashboard />
                    </AuthenticatedLayout>
                  </RequirePlan>
                </ProtectedRoute>
              }
            />

            {/* Settings Route */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <RequirePlan>
                    <AuthenticatedLayout>
                      <Settings />
                    </AuthenticatedLayout>
                  </RequirePlan>
                </ProtectedRoute>
              }
            />

            {/* Analytics Route - Premium Only */}
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <RequirePremium>
                    <AuthenticatedLayout>
                      <Analytics />
                    </AuthenticatedLayout>
                  </RequirePremium>
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path={ROUTES.QUESTIONS_MANAGEMENT}
              element={
                <PermissionRoute permission="VIEW_QUESTIONS_MANAGEMENT">
                  <AuthenticatedLayout>
                    <QuestionsManagement />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            <Route
              path={ROUTES.QUESTION_GENERATOR}
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <QuestionGenerator />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.QUESTION_DEMO}
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <QuestionDemo />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.EXAM}
              element={
                <PermissionRoute permission="VIEW_EXAM">
                  <RequirePlan>
                    <AuthenticatedLayout>
                      <ErrorBoundary>
                        <ExamDemo />
                      </ErrorBoundary>
                    </AuthenticatedLayout>
                  </RequirePlan>
                </PermissionRoute>
              }
            />
            <Route
              path={ROUTES.EXAM_HISTORY}
              element={
                <PermissionRoute permission="VIEW_EXAM_HISTORY">
                  <RequirePlan>
                    <AuthenticatedLayout>
                      <ErrorBoundary>
                        <ExamHistory />
                      </ErrorBoundary>
                    </AuthenticatedLayout>
                  </RequirePlan>
                </PermissionRoute>
              }
            />
            <Route
              path="/exam/session/:sessionId"
              element={
                <PermissionRoute permission="VIEW_EXAM">
                  <RequirePlan>
                    <ErrorBoundary>
                      <ExamSessionNBME />
                    </ErrorBoundary>
                  </RequirePlan>
                </PermissionRoute>
              }
            />
            <Route
              path="/exam/results/:sessionId"
              element={
                <PermissionRoute permission="VIEW_EXAM">
                  <RequirePlan>
                    <AuthenticatedLayout>
                      <ErrorBoundary>
                        <ExamResults />
                      </ErrorBoundary>
                    </AuthenticatedLayout>
                  </RequirePlan>
                </PermissionRoute>
              }
            />
            <Route
              path={ROUTES.REVIEW}
              element={
                <PermissionRoute permission="VIEW_REVIEW_QUEUE">
                  <AuthenticatedLayout>
                    <Review />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            <Route
              path="/test-exam-api"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <TestExamApi />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BUDGET}
              element={
                <PermissionRoute permission="VIEW_BUDGET">
                  <AuthenticatedLayout>
                    <Budget />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            <Route
              path={ROUTES.FINANCIAL}
              element={
                <PermissionRoute permission="VIEW_FINANCIAL">
                  <AuthenticatedLayout>
                    <FinancialDashboard />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <BillingPage />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/processing"
              element={
                <ProtectedRoute>
                  <BillingProcessing />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.USERS}
              element={
                <PermissionRoute permission="VIEW_USERS">
                  <AuthenticatedLayout>
                    <Users />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN}
              element={
                <PermissionRoute permission="VIEW_ADMIN">
                  <AuthenticatedLayout>
                    <AdminManagement />
                  </AuthenticatedLayout>
                </PermissionRoute>
              }
            />
            
            {/* Default Route - Now public, handles auth redirect internally */}
            <Route path="/" element={<DefaultRedirect />} />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </AuthProvider>
      </Router>
    </StytchProvider>
    <div className="hidden lg:block">
      <ReactQueryDevtools initialIsOpen={false} />
    </div>
    </QueryClientProvider>
  );
}

export default App;